import React from "react";
import { Reveal, SectionHeader } from "@/components/ui-blocks/Reveal";
import { ScrollText, Compass, HeartHandshake, ShieldCheck, Users, Leaf, GraduationCap, Sparkles } from "lucide-react";

const VALUES = [
  { title: "Integrity", desc: "We do the right thing — always, even when no one is watching.", Icon: ShieldCheck },
  { title: "Transparency", desc: "Every rupee, every voucher, every outcome — open by default.", Icon: ScrollText },
  { title: "Accountability", desc: "Named owners, published timelines, verifiable results.", Icon: Compass },
  { title: "Equality", desc: "Every citizen matters. No discrimination on any basis.", Icon: Users },
  { title: "Compassion", desc: "Kindness is a practice, not a slogan.", Icon: HeartHandshake },
  { title: "Community", desc: "Locally-led programs, backed by national systems.", Icon: Users },
  { title: "Education", desc: "We treat learning as the foundation for lasting change.", Icon: GraduationCap },
  { title: "Innovation", desc: "Technology, data and design serving human dignity.", Icon: Sparkles },
];

export const AboutMissionVision = () => (
  <section id="about" className="bg-background">
    <div className="container-wide py-16 lg:py-24">
      <div className="grid gap-12 lg:grid-cols-12 items-start">
        <div className="lg:col-span-5">
          <div className="eyebrow"><span className="inline-block h-1.5 w-6 rounded-full bg-[hsl(var(--green))]" /> About Us</div>
          <h2 className="heading-2 mt-3">A modern, non-partisan Indian NGO built for trust.</h2>
          <p className="lead mt-4">Citizen Awareness Foundation (CAF) is a citizen-led non-profit committed to public awareness, civic responsibility, and equitable development. We work with communities, governments and CSR partners — through peaceful, lawful, fact-based campaigns.</p>
          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            {[
              ["Registered", "Section 8 • 12A • 80G • CSR-1"],
              ["Incorporated", "Founded in 2026"],
              ["Reach", "18 villages • 6 states"],
              ["Audit", "First audit: FY 2026-27"],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-[hsl(var(--border))] p-3 bg-card">
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-ink))]">{k}</div>
                <div className="font-semibold text-[hsl(var(--navy))] mt-0.5">{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7 grid gap-5 md:grid-cols-2">
          <Reveal>
            <div className="rounded-xl border border-[hsl(var(--border))] border-t-4 border-t-[hsl(var(--orange))] p-6 h-full bg-[hsl(var(--surface-tint))]">
              <div className="eyebrow">Our Mission</div>
              <h3 className="heading-3 mt-2">Awareness that leads to action.</h3>
              <p className="text-sm text-[hsl(var(--muted-ink))] mt-3 leading-relaxed">To empower citizens — especially youth and underserved communities — with the awareness, tools and support to claim their rights, protect their environment, and improve their livelihoods.</p>
            </div>
          </Reveal>
          <Reveal delay={0.06}>
            <div className="rounded-xl border border-[hsl(var(--border))] border-t-4 border-t-[hsl(var(--green))] p-6 h-full bg-[hsl(var(--surface-tint-green))]">
              <div className="eyebrow">Our Vision</div>
              <h3 className="heading-3 mt-2">An informed India, in service of every Indian.</h3>
              <p className="text-sm text-[hsl(var(--muted-ink))] mt-3 leading-relaxed">A country where every citizen has the awareness and access to safety, justice, education, healthcare and dignified livelihoods.</p>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="md:col-span-2">
            <div className="rounded-xl border border-[hsl(var(--border))] p-6 bg-card">
              <div className="eyebrow mb-3">Core Values</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {VALUES.map(({ title, desc, Icon }) => (
                  <div key={title} className="" data-testid={`core-value-${title.toLowerCase()}`}>
                    <div className="h-9 w-9 rounded-md bg-[hsl(var(--surface-tint))] text-[hsl(var(--navy))] grid place-items-center"><Icon className="h-4.5 w-4.5" /></div>
                    <div className="mt-3 font-semibold text-[hsl(var(--navy))] text-sm">{title}</div>
                    <p className="text-xs text-[hsl(var(--muted-ink))] mt-1 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
